import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const Hero = () => {
    const { school_slug } = useParams();

    const [info, setInfo] = useState(null);

    // Controls when the video is actually added to the page
    const [loadVideo, setLoadVideo] = useState(false);

    // Controls when the video becomes visible
    const [videoReady, setVideoReady] = useState(false);

    const [isMobile, setIsMobile] = useState(
        () => window.innerWidth <= 768
    );

    const fallbackImage =
        `${baseAPIUrl}assets/images/home-page/image.jpg`;


    // =====================================================
    // GET SCHOOL SETTINGS
    // =====================================================

    useEffect(() => {

        const finalSchoolSlug =
            school_slug || "tigps-main";

        const controller = new AbortController();

        axios
            .get(
                `${baseAPIUrl}api/general_settings/get_general_settings/${finalSchoolSlug}`,
                {
                    signal: controller.signal
                }
            )
            .then((res) => {

                if (res.data.status === 1) {

                    setInfo(
                        res.data.data.settings
                    );

                }

            })
            .catch((err) => {

                if (err.code !== "ERR_CANCELED") {

                    console.error(
                        "GENERAL SETTINGS API ERROR:",
                        err
                    );

                }

            });


        return () => {

            controller.abort();

        };

    }, [school_slug]);


    // =====================================================
    // RESPONSIVE
    // =====================================================

    useEffect(() => {

        const handleResize = () => {

            setIsMobile(
                window.innerWidth <= 768
            );

        };


        window.addEventListener(
            "resize",
            handleResize
        );


        return () => {

            window.removeEventListener(
                "resize",
                handleResize
            );

        };

    }, []);


    // =====================================================
    // VIDEO URL
    // =====================================================

    const videoUrl = info?.school_video
        ? `${baseAPIUrl}assets/images/settings/${info.school_video}`
        : null;


    // =====================================================
    // LAZY LOAD VIDEO
    // =====================================================

    useEffect(() => {

        if (!videoUrl) return;


        // Check user's network connection
        const connection =
            navigator.connection ||
            navigator.mozConnection ||
            navigator.webkitConnection;


        // Don't download large video when
        // Data Saver is enabled
        if (connection?.saveData) {
            return;
        }


        // Don't download large video on
        // extremely slow connections
        if (
            connection?.effectiveType === "slow-2g" ||
            connection?.effectiveType === "2g"
        ) {
            return;
        }


        let timer = null;
        let idleId = null;


        const startVideoLoading = () => {

            /*
             * Desktop:
             * wait 800ms
             *
             * Mobile:
             * wait 2000ms
             */

            const delay =
                window.innerWidth <= 768
                    ? 2000
                    : 800;


            timer = setTimeout(() => {

                setLoadVideo(true);

            }, delay);

        };


        // Let important page resources load first

        if ("requestIdleCallback" in window) {

            idleId = window.requestIdleCallback(
                startVideoLoading,
                {
                    timeout: 2000
                }
            );

        } else {

            // Safari / older browser fallback

            startVideoLoading();

        }


        return () => {

            if (timer) {
                clearTimeout(timer);
            }


            if (
                idleId &&
                "cancelIdleCallback" in window
            ) {

                window.cancelIdleCallback(
                    idleId
                );

            }

        };

    }, [videoUrl]);


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <section
            style={{
                position: "relative",
                width: "100%",
                height: isMobile
                    ? "60vh"
                    : "75vh",

                minHeight: isMobile
                    ? "300px"
                    : "650px",

                maxHeight: isMobile
                    ? "none"
                    : "850px",

                overflow: "hidden",

                backgroundColor: "#000"
            }}
        >


            {/* ============================================
                FALLBACK IMAGE

                Displays immediately while video loads
            ============================================ */}

            <img
                src={fallbackImage}
                alt="TIGPS"
                loading="eager"
                fetchPriority="high"

                style={{
                    position: "absolute",

                    top: 0,
                    left: 0,

                    width: "100%",
                    height: "100%",

                    objectFit: "cover",
                    objectPosition: "center",

                    zIndex: 1
                }}
            />


            {/* ============================================
                VIDEO

                IMPORTANT:

                The video element doesn't exist until
                loadVideo === true.

                Therefore the browser cannot request
                the MP4 during initial page loading.
            ============================================ */}

            {videoUrl && loadVideo && (

                <video

                    autoPlay
                    muted
                    loop
                    playsInline

                    preload="metadata"

                    controls={false}

                    disablePictureInPicture

                    poster={fallbackImage}


                    // Video has enough data to start
                    onCanPlay={(e) => {

                        setVideoReady(true);

                        e.currentTarget
                            .play()
                            .catch(() => { });

                    }}


                    // If video fails, poster remains
                    onError={(e) => {

                        console.error(
                            "VIDEO LOAD ERROR:",
                            e
                        );

                        setVideoReady(false);

                    }}


                    style={{
                        position: "absolute",

                        top: 0,
                        left: 0,

                        width: "100%",
                        height: "100%",

                        objectFit: "cover",
                        objectPosition: "center",

                        zIndex: 2,

                        /*
                         * Keep video invisible until
                         * it can actually play.
                         */

                        opacity: videoReady
                            ? 1
                            : 0,

                        transition:
                            "opacity 0.5s ease-in-out"
                    }}
                >

                    <source
                        src={videoUrl}
                        type="video/mp4"
                    />

                </video>

            )}

        </section>

    );

};

export default Hero;